import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Card3D from './Card3D.js';
import useIntro from './useIntro.js';
import { menuItems, MODULE_SUBMENUS } from './Sidebar.js';
import {
  Users,
  Shield,
  ShieldCheck,
  Eye,
  Search,
  UserPlus,
  Check,
  X,
  ShieldAlert,
  EyeOff,
  ChevronDown,
  ChevronUp,
  XCircle,
  ChevronRight,
  Pencil,
  Trash2,
  AlertTriangle,
} from 'lucide-react';

const ACCESS_LEVELS = [
  { id: 'super_admin', label: 'S. Admin', short: 'S.Admin' },
  { id: 'admin', label: 'Admin', short: 'Admin' },
  { id: 'employee', label: 'Employee', short: 'Employee' },
];

function getInitials(name) {
  if (!name) return '??';
  return String(name)
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function effectiveRoleLabel(user) {
  if (!user) return '—';
  if (user.is_super_admin) return 'Super Admin';
  if (user.role === 'admin') return 'Admin';
  if (user.role === 'employee') return 'Employee';
  return user.role || '—';
}

function formatDate(v) {
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '—';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `enrolled ${yyyy}-${mm}-${dd}`;
  } catch (_) {
    return '—';
  }
}

function cloneModuleAccess(ma) {
  const out = {};
  for (const key of Object.keys(ma || {})) {
    const entry = ma[key] || {};
    const subCloned = {};
    if (entry.submenu_access && typeof entry.submenu_access === 'object') {
      for (const sk of Object.keys(entry.submenu_access)) {
        subCloned[sk] = { ...entry.submenu_access[sk] };
      }
    }
    out[key] = { ...entry, submenu_access: subCloned };
  }
  return out;
}

const SETTINGS_OWNER_EMAILS = new Set(['vinsimon23@gmail.com', 'vinsimon@gmail.com'].map((s) => s.toLowerCase()));
function isSettingsOwnerOf(emailOrUser) {
  if (!emailOrUser) return false;
  if (typeof emailOrUser === 'string') {
    const e = String(emailOrUser).toLowerCase();
    return SETTINGS_OWNER_EMAILS.has(e);
  }
  if (typeof emailOrUser === 'object') {
    if (!!emailOrUser.is_super_admin) return true;
    const e = String(emailOrUser.email || '').toLowerCase();
    return SETTINGS_OWNER_EMAILS.has(e);
  }
  return false;
}

function defaultModuleAccessFor(role, is_sa, targetEmail) {
  const isOwner = isSettingsOwnerOf(targetEmail);
  const out = {};
  for (const item of menuItems) {
    const mid = item.id;
    const subDef = {};
    const submenus = MODULE_SUBMENUS[mid] || [];
    for (const sm of submenus) {
      subDef[sm.id] = { visible: true };
    }
    const settingsVisible = isOwner;
    const settingsSub = mid === 'settings'
      ? Object.fromEntries(Object.keys(subDef).map((k) => [k, { visible: settingsVisible }]))
      : subDef;
    if (is_sa) {
      out[mid] = {
        visible: mid === 'settings' ? settingsVisible : true,
        access_level: 'super_admin',
        submenu_access: settingsSub,
      };
    } else if (role === 'admin') {
      out[mid] = {
        visible: mid === 'settings' ? settingsVisible : true,
        access_level: 'admin',
        submenu_access: settingsSub,
      };
    } else {
      const moduleVisible = mid === 'settings'
        ? settingsVisible
        : ['dashboard', 'mapview', 'clockora', 'announcements'].includes(mid);
      out[mid] = {
        visible: moduleVisible,
        access_level: 'employee',
        submenu_access: settingsSub,
      };
    }
  }
  return out;
}

function StatCard({ title, value, icon: Icon, accent, staggerIdx }) {
  const isIntro = useIntro();
  const s = isIntro ? { className: 'stagger-in', style: { '--i': staggerIdx } } : {};
  return (
    <div className={s.className} style={s.style}>
      <Card3D className="content-glass rounded-xl p-4 flex items-center justify-between" intensity={5} lift={10} tilt={false}>
        <div className="min-w-0">
          <p
            className="text-[10px] font-semibold uppercase tracking-widest mb-1"
            style={{ color: 'var(--nad-t4)' }}
          >
            {title}
          </p>
          <p className="text-2xl font-extrabold tracking-tight" style={{ color: 'var(--nad-t1)' }}>
            {value}
          </p>
        </div>
        <div
          className="w-9 h-9 rounded-xl border flex items-center justify-center flex-shrink-0"
          style={{
            borderColor: 'var(--nad-b2)',
            background: 'linear-gradient(135deg, rgba(59,130,246,0.18), rgba(250,204,21,0.14))',
            color: accent || 'var(--nad-accent-txt)',
          }}
        >
          <Icon size={17} strokeWidth={2.2} />
        </div>
      </Card3D>
    </div>
  );
}

function SegmentedPill({ options, value, onChange, disabled }) {
  return (
    <div
      className="inline-flex rounded-full p-0.5"
      style={{
        background: 'var(--nad-s1)',
        border: '1px solid var(--nad-b2)',
      }}
    >
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onChange(opt.id)}
            className="relative px-3 py-1 rounded-full text-[11px] font-semibold transition-all duration-200"
            style={{
              background: active
                ? 'linear-gradient(135deg, rgba(250,204,21,0.35), rgba(59,130,246,0.25))'
                : 'transparent',
              color: active ? 'var(--nad-accent-txt)' : 'var(--nad-t3)',
              border: active ? '1px solid rgba(250,204,21,0.35)' : '1px solid transparent',
              boxShadow: active ? '0 0 0 1px rgba(250,204,21,0.18) inset' : undefined,
              opacity: disabled ? 0.5 : 1,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function CircleSwitch({ checked, onChange, disabled, size = 'md' }) {
  const [pressed, setPressed] = useState(false);
  const isSm = size === 'sm';
  const w = isSm ? 32 : 44;
  const h = isSm ? 18 : 24;
  const knob = isSm ? 13 : 18;
  const knobLeft = isSm ? 2 : 3;
  const knobRightOffset = isSm ? 16 : 22;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      className="relative transition-transform duration-200"
      style={{
        width: w,
        height: h,
        borderRadius: 999,
        background: checked
          ? 'linear-gradient(90deg, #34d399, #10b981)'
          : 'var(--nad-s2)',
        border: '1px solid',
        borderColor: checked ? 'rgba(16,185,129,0.6)' : 'var(--nad-b2)',
        boxShadow: checked
          ? `0 0 0 3px rgba(16,185,129,0.15), 0 6px 16px -4px rgba(16,185,129,0.45)`
          : undefined,
        transform: pressed ? 'scale(0.96)' : 'scale(1)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <span
        className="absolute top-[50%] -translate-y-1/2 transition-all duration-300"
        style={{
          width: knob,
          height: knob,
          borderRadius: '50%',
          background: '#fff',
          left: checked ? `calc(100% - ${knobRightOffset}px)` : knobLeft,
          boxShadow: '0 2px 6px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.04) inset',
        }}
      />
    </button>
  );
}

function TinyPill({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-2.5 py-1 rounded-full text-[10px] font-bold transition-all duration-200"
      style={{
        background: active
          ? 'linear-gradient(135deg, rgba(250,204,21,0.28), rgba(59,130,246,0.18))'
          : 'var(--nad-s2)',
        color: active ? 'var(--nad-accent-txt)' : 'var(--nad-t3)',
        border: '1px solid',
        borderColor: active ? 'rgba(250,204,21,0.45)' : 'var(--nad-b2)',
      }}
    >
      {label}
    </button>
  );
}

function RowAccessLevelPills({ currentLevel, onChange, disabled }) {
  return (
    <div className="flex items-center gap-1.5">
      {ACCESS_LEVELS.map((lvl) => {
        const active = currentLevel === lvl.id;
        return (
          <button
            key={lvl.id}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onChange(lvl.id)}
            className="px-2.5 py-1 rounded-md text-[10px] font-bold transition-all duration-200"
            style={{
              background: active
                ? 'linear-gradient(135deg, rgba(250,204,21,0.35), rgba(59,130,246,0.22))'
                : 'var(--nad-s2)',
              color: active ? 'var(--nad-accent-txt)' : 'var(--nad-t3)',
              border: '1px solid',
              borderColor: active ? 'rgba(250,204,21,0.5)' : 'var(--nad-b2)',
              opacity: disabled ? 0.5 : 1,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {lvl.id === 'super_admin' ? 'S.Admin' : lvl.short}
          </button>
        );
      })}
    </div>
  );
}

function UserRow({ user, selected, onClick, isSelf, onEdit, onDelete, editing, deleting }) {
  const initials = getInitials(user.full_name);
  const label = effectiveRoleLabel(user);
  const active = user.is_active;
  const initialsColor =
    user.is_super_admin
      ? 'from-amber-500 to-yellow-400'
      : user.role === 'admin'
      ? 'from-blue-500 to-sky-400'
      : 'from-emerald-500 to-teal-400';
  const isOwner = isSettingsOwnerOf(user);
  const stop = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };
  return (
    <div
      onClick={onClick}
      className="w-full rounded-xl transition-all duration-200 cursor-pointer"
      style={{
        background: selected
          ? 'linear-gradient(to right, rgba(250,204,21,0.12), rgba(59,130,246,0.08))'
          : 'transparent',
        border: selected ? '1.5px solid rgba(250,204,21,0.55)' : '1.5px solid transparent',
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.background = 'var(--nad-s1)';
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.background = 'transparent';
      }}
    >
      <div className="flex items-center gap-3 px-3 py-2.5 text-left">
        <div
          className={`w-9 h-9 rounded-lg bg-gradient-to-br ${initialsColor} flex items-center justify-center flex-shrink-0 ring-2 ring-white/10`}
        >
          <span className="text-white font-bold text-[13px]">{initials}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[13px] font-semibold truncate" style={{ color: 'var(--nad-t1)' }}>
              {user.full_name || '—'}
              {isSelf && (
                <span
                  className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wide uppercase"
                  style={{
                    background: 'rgba(59,130,246,0.2)',
                    color: 'var(--nad-accent-txt)',
                    border: '1px solid rgba(59,130,246,0.35)',
                  }}
                >
                  You
                </span>
              )}
              {isOwner && !isSelf && (
                <span
                  className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wide uppercase"
                  style={{
                    background: 'rgba(250,204,21,0.22)',
                    color: '#f59e0b',
                    border: '1px solid rgba(250,204,21,0.4)',
                  }}
                >
                  OWNER
                </span>
              )}
            </p>
          </div>
          <p className="text-[11px] truncate" style={{ color: 'var(--nad-t3)' }}>
            {label}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <div className="pr-1">
            {active ? (
              user.is_super_admin ? (
                <ShieldCheck size={16} strokeWidth={2.2} style={{ color: 'var(--nad-accent-txt)' }} />
              ) : user.role === 'admin' ? (
                <Shield size={16} strokeWidth={2.2} style={{ color: '#60a5fa' }} />
              ) : (
                <Eye size={16} strokeWidth={2.2} style={{ color: '#34d399' }} />
              )
            ) : (
              <span
                className="px-1.5 py-0.5 rounded text-[9px] font-extrabold tracking-widest"
                style={{
                  color: '#fca5a5',
                  background: 'rgba(248,113,113,0.14)',
                  border: '1px solid rgba(248,113,113,0.35)',
                }}
              >
                OFF
              </span>
            )}
          </div>
          <button
            type="button"
            title="Edit user"
            onClick={(e) => {
              stop(e);
              onEdit && onEdit(user);
            }}
            disabled={editing}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
            style={{
              background: 'var(--nad-s1)',
              color: 'var(--nad-t2)',
              border: '1px solid var(--nad-b2)',
              opacity: editing ? 0.5 : 1,
            }}
          >
            <Pencil size={13} />
          </button>
          <button
            type="button"
            title="Delete user"
            onClick={(e) => {
              stop(e);
              onDelete && onDelete(user);
            }}
            disabled={deleting || isOwner}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
            style={{
              background: isOwner ? 'rgba(248,113,113,0.08)' : 'var(--nad-s1)',
              color: isOwner ? '#f87171' : '#f87171',
              border: '1px solid',
              borderColor: isOwner ? 'rgba(248,113,113,0.3)' : 'var(--nad-b2)',
              opacity: deleting ? 0.5 : 1,
              cursor: isOwner ? 'not-allowed' : 'pointer',
            }}
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

function ModuleAccessRow({ item, access, onChange, onSubmenuChange, targetIsSettingsOwner, expanded, onToggleExpand }) {
  const Icon = item.icon;
  const isSettings = item.id === 'settings';
  const settingsReserved = isSettings && !targetIsSettingsOwner;
  const effectiveVisible = settingsReserved ? false : (access?.visible ?? true);
  const effectiveLevel = settingsReserved
    ? 'admin'
    : access?.access_level || 'employee';
  const disabled = settingsReserved;
  const submenus = MODULE_SUBMENUS[item.id] || [];
  const submenu_access = (access?.submenu_access) || {};
  const totalVisible = submenus.reduce(
    (acc, sm) => acc + (submenu_access[sm.id]?.visible !== false ? 1 : 0),
    0
  );

  return (
    <div
      className="rounded-xl transition-colors overflow-hidden"
      style={{ border: '1px solid var(--nad-b2)', background: 'var(--nad-s1)' }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--nad-s2)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--nad-s1)')}
    >
      <div className="flex items-center gap-3 px-3 py-2.5">
        <button
          type="button"
          onClick={() => onToggleExpand && submenus.length > 0 && onToggleExpand(item.id)}
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
          style={{
            background: submenus.length > 0 ? 'var(--nad-s2)' : 'transparent',
            color: 'var(--nad-t3)',
            opacity: submenus.length > 0 ? 1 : 0,
            pointerEvents: submenus.length > 0 ? 'auto' : 'none',
          }}
          aria-label={expanded ? 'Collapse submenu access' : 'Expand submenu access'}
          title={submenus.length > 0 ? `Submenus: ${totalVisible}/${submenus.length} visible` : ''}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <div
          className="w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0"
          style={{
            borderColor: 'var(--nad-b2)',
            background: 'linear-gradient(135deg, rgba(59,130,246,0.18), rgba(250,204,21,0.12))',
            color: 'var(--nad-accent-txt)',
          }}
        >
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p
              className="min-w-0 text-[13px] font-medium truncate"
              style={{ color: 'var(--nad-t1)' }}
            >
              {item.label}
              {settingsReserved && (
                <span
                  className="ml-2 text-[9.5px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{
                    color: '#fbbf24',
                    border: '1px solid rgba(251,191,36,0.4)',
                    background: 'rgba(251,191,36,0.08)',
                  }}
                >
                  OWNER ONLY
                </span>
              )}
            </p>
          </div>
          {submenus.length > 0 && (
            <p className="text-[10px] truncate" style={{ color: 'var(--nad-t4)' }}>
              {totalVisible} of {submenus.length} submenus visible
            </p>
          )}
        </div>
        <RowAccessLevelPills
          currentLevel={effectiveLevel}
          onChange={(newLvl) =>
            !disabled &&
            onChange(item.id, { visible: effectiveVisible, access_level: newLvl })
          }
          disabled={disabled}
        />
        <CircleSwitch
          checked={effectiveVisible}
          onChange={(v) =>
            !disabled &&
            onChange(item.id, { visible: v, access_level: effectiveLevel })
          }
          disabled={disabled}
        />
      </div>
      {expanded && submenus.length > 0 && (
        <div
          className="px-3 pb-3 pt-1"
          style={{ borderTop: '1px solid var(--nad-b2)' }}
        >
          <div
            className="mb-2 pt-2 flex items-center justify-between"
          >
            <p
              className="text-[10px] font-bold uppercase tracking-widest"
              style={{ color: 'var(--nad-t4)' }}
            >
              Submenu Access
            </p>
            <div className="flex gap-1.5">
              <TinyPill
                label="All"
                active={false}
                onClick={() => {
                  if (disabled) return;
                  submenus.forEach((sm) =>
                    onSubmenuChange(item.id, sm.id, { visible: true })
                  );
                }}
              />
              <TinyPill
                label="None"
                active={false}
                onClick={() => {
                  if (disabled) return;
                  submenus.forEach((sm) =>
                    onSubmenuChange(item.id, sm.id, { visible: false })
                  );
                }}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            {submenus.map((sm) => {
              const smVisible = submenu_access[sm.id]?.visible !== false;
              return (
                <div
                  key={sm.id}
                  className="flex items-center gap-3 px-2 py-1.5 rounded-lg"
                  style={{
                    background: 'var(--nad-s2)',
                    border: '1px solid var(--nad-b1)',
                  }}
                >
                  <div
                    className="ml-1 w-1 h-1 rounded-full flex-shrink-0"
                    style={{ background: 'var(--nad-t4)' }}
                  />
                  <p
                    className="flex-1 min-w-0 text-[12px] truncate"
                    style={{ color: 'var(--nad-t2)' }}
                  >
                    {sm.label}
                  </p>
                  <CircleSwitch
                    size="sm"
                    checked={smVisible}
                    onChange={(v) =>
                      !disabled && onSubmenuChange(item.id, sm.id, { visible: v })
                    }
                    disabled={disabled}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function EnrollDialog({ open, onClose, onSubmit, submitting }) {
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'admin',
    is_super_admin: false,
  });

  useEffect(() => {
    if (open) {
      setForm({
        full_name: '',
        email: '',
        password: '',
        role: 'admin',
        is_super_admin: false,
      });
    }
  }, [open]);

  if (!open) return null;

  const valid =
    form.full_name.trim().length > 0 &&
    /.+@.+\..+/.test(form.email || '') &&
    (form.password || '').length >= 6;

  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    onSubmit({ ...form });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 backdrop-blur-sm"
        style={{ background: 'rgba(2,6,23,0.7)' }}
        onClick={onClose}
      />
      <div className="relative w-full max-w-md">
        <Card3D className="content-glass rounded-2xl p-6" intensity={6} lift={18} tilt={false}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold" style={{ color: 'var(--nad-t1)' }}>
                Enroll New User
              </h3>
              <p className="text-[12px] mt-1" style={{ color: 'var(--nad-t3)' }}>
                Add a new admin user. You can configure per-module access and per-submenu visibility in Side Panel Access after enrollment.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: 'var(--nad-s1)', color: 'var(--nad-t3)', border: '1px solid var(--nad-b2)' }}
            >
              <X size={14} />
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-[11px] font-semibold mb-1.5 tracking-wide" style={{ color: 'var(--nad-t4)' }}>
                FULL NAME
              </label>
              <input
                type="text"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none"
                placeholder="e.g. Maria Santos"
                style={{
                  background: 'var(--nad-s1)',
                  color: 'var(--nad-t1)',
                  border: '1px solid var(--nad-b2)',
                }}
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold mb-1.5 tracking-wide" style={{ color: 'var(--nad-t4)' }}>
                EMAIL
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none"
                placeholder="name@company.com"
                style={{
                  background: 'var(--nad-s1)',
                  color: 'var(--nad-t1)',
                  border: '1px solid var(--nad-b2)',
                }}
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold mb-1.5 tracking-wide" style={{ color: 'var(--nad-t4)' }}>
                PASSWORD
              </label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none"
                placeholder="At least 6 characters"
                style={{
                  background: 'var(--nad-s1)',
                  color: 'var(--nad-t1)',
                  border: '1px solid var(--nad-b2)',
                }}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-semibold"
                style={{
                  background: 'var(--nad-s1)',
                  color: 'var(--nad-t2)',
                  border: '1px solid var(--nad-b2)',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!valid || submitting}
                className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-bold transition-all duration-200 flex items-center justify-center gap-1.5"
                style={{
                  background: valid
                    ? 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                    : 'var(--nad-s2)',
                  color: valid ? '#1e293b' : 'var(--nad-t3)',
                  border: '1px solid',
                  borderColor: valid ? 'rgba(250,204,21,0.6)' : 'var(--nad-b2)',
                  boxShadow: valid ? '0 10px 24px -8px rgba(250,204,21,0.45)' : undefined,
                  opacity: submitting ? 0.7 : 1,
                  cursor: !valid ? 'not-allowed' : 'pointer',
                }}
              >
                <UserPlus size={14} />
                {submitting ? 'Enrolling…' : 'Enroll'}
              </button>
            </div>
          </form>
        </Card3D>
      </div>
    </div>
  );
}

function EditUserDialog({ open, onClose, onSubmit, submitting, user }) {
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'employee',
    is_super_admin: false,
  });

  useEffect(() => {
    if (open && user) {
      setForm({
        full_name: user.full_name || '',
        email: user.email || '',
        password: '',
        role: user.role === 'super_admin' ? 'admin' : user.role || 'employee',
        is_super_admin: !!user.is_super_admin,
      });
    }
  }, [open, user]);

  if (!open) return null;

  const isSuper = form.is_super_admin;

  const valid =
    form.full_name.trim().length > 0 &&
    /.+@.+\..+/.test(form.email || '') &&
    (form.password ? form.password.length >= 6 : true);

  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    const payload = {
      full_name: form.full_name,
      email: form.email,
      role: form.role,
      is_super_admin: form.is_super_admin,
    };
    if (form.password) payload.password = form.password;
    onSubmit(payload);
  };

  const isOwner = isSettingsOwnerOf(user);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 backdrop-blur-sm"
        style={{ background: 'rgba(2,6,23,0.7)' }}
        onClick={onClose}
      />
      <div className="relative w-full max-w-md">
        <Card3D className="content-glass rounded-2xl p-6" intensity={6} lift={18} tilt={false}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold" style={{ color: 'var(--nad-t1)' }}>
                Edit User
              </h3>
              <p className="text-[12px] mt-1" style={{ color: 'var(--nad-t3)' }}>
                Update this user's identity, credentials, and account type.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: 'var(--nad-s1)', color: 'var(--nad-t3)', border: '1px solid var(--nad-b2)' }}
            >
              <X size={14} />
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-[11px] font-semibold mb-1.5 tracking-wide" style={{ color: 'var(--nad-t4)' }}>
                FULL NAME
              </label>
              <input
                type="text"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none"
                placeholder="e.g. Maria Santos"
                style={{
                  background: 'var(--nad-s1)',
                  color: 'var(--nad-t1)',
                  border: '1px solid var(--nad-b2)',
                }}
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold mb-1.5 tracking-wide" style={{ color: 'var(--nad-t4)' }}>
                EMAIL
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none"
                placeholder="name@company.com"
                style={{
                  background: 'var(--nad-s1)',
                  color: 'var(--nad-t1)',
                  border: '1px solid var(--nad-b2)',
                }}
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold mb-1.5 tracking-wide" style={{ color: 'var(--nad-t4)' }}>
                NEW PASSWORD <span style={{ opacity: 0.6, fontWeight: 500 }}>(leave blank to keep current)</span>
              </label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none"
                placeholder="At least 6 characters"
                style={{
                  background: 'var(--nad-s1)',
                  color: 'var(--nad-t1)',
                  border: '1px solid var(--nad-b2)',
                }}
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold mb-2 tracking-wide" style={{ color: 'var(--nad-t4)' }}>
                ACCOUNT TYPE
              </label>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between px-3 py-2 rounded-xl" style={{ background: 'var(--nad-s1)', border: '1px solid var(--nad-b2)' }}>
                  <div className="flex items-center gap-2">
                    <ShieldAlert size={15} style={{ color: 'var(--nad-accent-txt)' }} />
                    <div>
                      <p className="text-[12px] font-bold" style={{ color: 'var(--nad-t1)' }}>Super Admin</p>
                      <p className="text-[10px]" style={{ color: 'var(--nad-t3)' }}>Full access to every module</p>
                    </div>
                  </div>
                  <CircleSwitch
                    checked={isSuper}
                    onChange={(v) =>
                      setForm({ ...form, is_super_admin: v, role: v ? 'admin' : form.role })
                    }
                  />
                </div>
                <div className="flex items-center justify-between px-3 py-2 rounded-xl" style={{ background: 'var(--nad-s1)', border: '1px solid var(--nad-b2)' }}>
                  <div className="flex items-center gap-2">
                    <Shield size={15} style={{ color: '#60a5fa' }} />
                    <div>
                      <p className="text-[12px] font-bold" style={{ color: 'var(--nad-t1)' }}>Admin</p>
                      <p className="text-[10px]" style={{ color: 'var(--nad-t3)' }}>Limited create/edit access</p>
                    </div>
                  </div>
                  <CircleSwitch
                    checked={!isSuper && form.role === 'admin'}
                    onChange={(v) => setForm({ ...form, role: v ? 'admin' : 'admin', is_super_admin: false })}
                    disabled={isSuper || isOwner}
                  />
                </div>
                <div className="flex items-center justify-between px-3 py-2 rounded-xl" style={{ background: 'var(--nad-s1)', border: '1px solid var(--nad-b2)', opacity: 0.55 }}>
                  <div className="flex items-center gap-2">
                    <Eye size={15} style={{ color: '#34d399' }} />
                    <div>
                      <p className="text-[12px] font-bold" style={{ color: 'var(--nad-t1)' }}>Employee</p>
                      <p className="text-[10px]" style={{ color: 'var(--nad-t3)' }}>Not available from Settings — edit employees from Humacore</p>
                    </div>
                  </div>
                  <CircleSwitch
                    checked={false}
                    onChange={() => {}}
                    disabled
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-semibold"
                style={{
                  background: 'var(--nad-s1)',
                  color: 'var(--nad-t2)',
                  border: '1px solid var(--nad-b2)',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!valid || submitting}
                className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-bold transition-all duration-200 flex items-center justify-center gap-1.5"
                style={{
                  background: valid
                    ? 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                    : 'var(--nad-s2)',
                  color: valid ? '#1e293b' : 'var(--nad-t3)',
                  border: '1px solid',
                  borderColor: valid ? 'rgba(250,204,21,0.6)' : 'var(--nad-b2)',
                  boxShadow: valid ? '0 10px 24px -8px rgba(250,204,21,0.45)' : undefined,
                  opacity: submitting ? 0.7 : 1,
                  cursor: !valid ? 'not-allowed' : 'pointer',
                }}
              >
                <Check size={14} />
                {submitting ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Card3D>
      </div>
    </div>
  );
}

function DeleteUserDialog({ open, onClose, onConfirm, confirming, user }) {
  if (!open || !user) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 backdrop-blur-sm"
        style={{ background: 'rgba(2,6,23,0.75)' }}
        onClick={onClose}
      />
      <div className="relative w-full max-w-md">
        <Card3D className="content-glass rounded-2xl p-6" intensity={6} lift={18} tilt={false}>
          <div className="flex items-start gap-3 mb-4">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(248,113,113,0.18)', color: '#f87171', border: '1px solid rgba(248,113,113,0.4)' }}
            >
              <AlertTriangle size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-[15px] font-bold" style={{ color: 'var(--nad-t1)' }}>
                Delete user?
              </h3>
              <p className="text-[12px] mt-1" style={{ color: 'var(--nad-t3)' }}>
                This will permanently remove <span style={{ color: '#fecaca', fontWeight: 700 }}>{user.full_name || 'this user'}</span> ({user.email || '—'}). This action cannot be undone.
              </p>
            </div>
          </div>

          <div
            className="rounded-xl p-3 mb-4 text-[11.5px]"
            style={{
              background: 'rgba(248,113,113,0.08)',
              border: '1px solid rgba(248,113,113,0.25)',
              color: '#fca5a5',
            }}
          >
            <div className="flex items-start gap-2">
              <ShieldAlert size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <p className="font-bold mb-0.5">Security checks enforced by the server</p>
                <ul className="list-disc pl-4 space-y-0.5 opacity-95">
                  <li>You cannot delete the Settings owner account.</li>
                  <li>You cannot delete the last remaining active Super Admin.</li>
                  <li>If any of these apply, the request will be rejected.</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-semibold"
              style={{
                background: 'var(--nad-s1)',
                color: 'var(--nad-t2)',
                border: '1px solid var(--nad-b2)',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={confirming}
              className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-bold transition-all duration-200 flex items-center justify-center gap-1.5"
              style={{
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: '#fff',
                border: '1px solid rgba(239,68,68,0.55)',
                boxShadow: '0 10px 24px -8px rgba(239,68,68,0.45)',
                opacity: confirming ? 0.7 : 1,
              }}
            >
              <Trash2 size={14} />
              {confirming ? 'Deleting…' : 'Confirm Delete'}
            </button>
          </div>
        </Card3D>
      </div>
    </div>
  );
}

export default function SettingsPage({ user, axiosInstance }) {
  const isIntro = useIntro();
  const stagger = (i) =>
    isIntro ? { className: 'stagger-in', style: { '--i': i } } : {};

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);

  const toggleRowExpanded = useCallback((moduleId) => {
    setExpandedRows((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/users/all');
      const list = Array.isArray(res?.data) ? res.data : [];
      setUsers(list);
      const adminList = list.filter((u) => u.role === 'admin' || !!u.is_super_admin);
      if (adminList.length > 0) {
        const me = adminList.find((u) => u.id === user?.id) || adminList[0];
        setSelectedId(me.id);
      } else {
        setSelectedId(null);
      }
    } catch (e) {
      console.error('Failed to load users', e);
      setError('Failed to load users. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, user?.id]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const adminUsers = useMemo(
    () => users.filter((u) => u.role === 'admin' || !!u.is_super_admin),
    [users]
  );

  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return adminUsers;
    return adminUsers.filter(
      (u) =>
        (u.full_name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.position || '').toLowerCase().includes(q) ||
        (u.department || '').toLowerCase().includes(q)
    );
  }, [adminUsers, search]);

  const counts = useMemo(() => {
    const total = adminUsers.length;
    const superAdmins = adminUsers.filter((u) => u.is_super_admin).length;
    const admins = adminUsers.filter((u) => !u.is_super_admin && u.role === 'admin').length;
    const employees = users.filter((u) => !u.is_super_admin && u.role === 'employee').length;
    return { total, superAdmins, admins, employees };
  }, [adminUsers, users]);

  const selected = useMemo(
    () => adminUsers.find((u) => u.id === selectedId) || null,
    [adminUsers, selectedId]
  );

  // Initialize draft when selection changes
  useEffect(() => {
    if (!selected) {
      setDraft(null);
      return;
    }
    setDraft({
      role: selected.is_super_admin ? 'admin' : selected.role || 'employee',
      is_super_admin: !!selected.is_super_admin,
      is_active: selected.is_active !== false,
      module_access: cloneModuleAccess(selected.module_access || defaultModuleAccessFor(selected.role, !!selected.is_super_admin, selected.email)),
    });
  }, [selected?.id, selected?.module_access, selected?.role, selected?.is_super_admin, selected?.is_active]);

  const visibleCount = useMemo(() => {
    if (!draft?.module_access) return { visible: 0, total: menuItems.length };
    let v = 0;
    for (const item of menuItems) if (draft.module_access[item.id]?.visible) v++;
    return { visible: v, total: menuItems.length };
  }, [draft?.module_access]);

  const selectedIsSelf = selected?.id === user?.id;

  const handleToggleActive = (v) => {
    if (!draft) return;
    setDraft({ ...draft, is_active: v });
  };

  const handleModuleChange = (moduleId, next) => {
    if (!draft) return;
    const ma = cloneModuleAccess(draft.module_access || {});
    const prev = ma[moduleId] || { visible: true, access_level: 'employee', submenu_access: {} };
    const submenus = MODULE_SUBMENUS[moduleId] || [];
    const prevSub = prev.submenu_access || {};
    const defaultedSub = {};
    for (const sm of submenus) {
      defaultedSub[sm.id] = prevSub[sm.id] || { visible: true };
    }
    ma[moduleId] = { ...prev, submenu_access: defaultedSub, ...next };
    setDraft({ ...draft, module_access: ma });
  };

  const handleSubmenuChange = (moduleId, submenuId, next) => {
    if (!draft) return;
    const ma = cloneModuleAccess(draft.module_access || {});
    const prev = ma[moduleId] || { visible: true, access_level: 'employee', submenu_access: {} };
    const prevSub = prev.submenu_access || {};
    const prevThis = prevSub[submenuId] || { visible: true };
    ma[moduleId] = {
      ...prev,
      submenu_access: { ...prevSub, [submenuId]: { ...prevThis, ...next } },
    };
    setDraft({ ...draft, module_access: ma });
  };

  const handleBulkVisible = (visible) => {
    if (!draft) return;
    const ma = cloneModuleAccess(draft.module_access || {});
    const targetIsSettingsOwner = isSettingsOwnerOf(selected);
    for (const item of menuItems) {
      const isSettings = item.id === 'settings';
      if (isSettings && !targetIsSettingsOwner) continue;
      const prev = ma[item.id] || { visible: true, access_level: 'employee', submenu_access: {} };
      const submenus = MODULE_SUBMENUS[item.id] || [];
      const prevSub = prev.submenu_access || {};
      const defaultedSub = {};
      for (const sm of submenus) {
        const orig = prevSub[sm.id] || { visible: true };
        defaultedSub[sm.id] = { ...orig, visible };
      }
      ma[item.id] = { ...prev, visible, submenu_access: defaultedSub };
    }
    setDraft({ ...draft, module_access: ma });
  };

  const hasChanges = useMemo(() => {
    if (!selected || !draft) return false;
    const nextRole = draft.is_super_admin ? 'admin' : draft.role;
    const currRole = selected.is_super_admin ? 'admin' : selected.role;
    if (nextRole !== currRole) return true;
    if (!!draft.is_super_admin !== !!selected.is_super_admin) return true;
    if (!!draft.is_active !== !!selected.is_active) return true;
    const baseMa = selected.module_access || {};
    for (const item of menuItems) {
      const a = baseMa[item.id] || {};
      const b = draft.module_access?.[item.id] || {};
      if (!!a.visible !== !!b.visible) return true;
      if ((a.access_level || 'employee') !== (b.access_level || 'employee')) return true;
      const aSub = a.submenu_access || {};
      const bSub = b.submenu_access || {};
      const submenus = MODULE_SUBMENUS[item.id] || [];
      for (const sm of submenus) {
        const as = aSub[sm.id];
        const bs = bSub[sm.id];
        if (!!as?.visible !== !!bs?.visible) return true;
      }
    }
    return false;
  }, [selected, draft]);

  const saveChanges = async () => {
    if (!selected || !draft) return;
    try {
      setSaving(true);
      setError(null);
      const targetIsSettingsOwner = isSettingsOwnerOf(selected);
      const moduleAccessCloned = cloneModuleAccess(draft.module_access);
      if (targetIsSettingsOwner) {
        const prevSettings = moduleAccessCloned.settings || {
          visible: true,
          access_level: 'super_admin',
          submenu_access: {},
        };
        const settingsSub = Object.fromEntries(
          Object.keys(prevSettings.submenu_access || {}).length
            ? Object.keys(prevSettings.submenu_access).map((k) => [k, { visible: true }])
            : (() => {
                const subDef = {};
                for (const sm of MODULE_SUBMENUS.settings || []) {
                  subDef[sm.id] = { visible: true };
                }
                return subDef;
              })()
        );
        moduleAccessCloned.settings = {
          ...prevSettings,
          visible: true,
          submenu_access: settingsSub,
        };
      }
      const payload = {
        role: draft.role,
        is_super_admin: draft.is_super_admin,
        is_active: draft.is_active,
        module_access: moduleAccessCloned,
      };
      const res = await axiosInstance.put(`/users/${selected.id}`, payload);
      const updated = res.data;
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      if (user && user.id === updated.id) {
        try {
          localStorage.setItem('user', JSON.stringify(updated));
        } catch (_) {}
        try {
          const meRes = await axiosInstance.get('/auth/me');
          if (meRes?.data) {
            localStorage.setItem('user', JSON.stringify(meRes.data));
          }
        } catch (_) {}
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to save changes.';
      setError(msg);
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleEnroll = async (form) => {
    try {
      setEnrolling(true);
      setError(null);
      const res = await axiosInstance.post('/users', {
        full_name: form.full_name,
        email: form.email,
        password: form.password,
        role: form.role,
        is_super_admin: form.is_super_admin,
      });
      const newUser = res.data;
      setUsers((prev) => [newUser, ...prev]);
      setSelectedId(newUser.id);
      setEnrollOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to enroll user.';
      setError(msg);
      console.error(e);
    } finally {
      setEnrolling(false);
    }
  };

  const openEdit = useCallback((u) => {
    setEditTarget(u);
    setEditOpen(true);
  }, []);

  const handleEditSubmit = async (form) => {
    if (!editTarget) return;
    try {
      setEditSaving(true);
      setError(null);
      const res = await axiosInstance.put(`/users/${editTarget.id}`, form);
      const updated = res.data;
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      if (user && user.id === updated.id) {
        try {
          localStorage.setItem('user', JSON.stringify(updated));
        } catch (_) {}
        try {
          const meRes = await axiosInstance.get('/auth/me');
          if (meRes?.data) {
            localStorage.setItem('user', JSON.stringify(meRes.data));
          }
        } catch (_) {}
      }
      setEditOpen(false);
      setEditTarget(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to update user.';
      setError(msg);
      console.error(e);
    } finally {
      setEditSaving(false);
    }
  };

  const openDelete = useCallback((u) => {
    setDeleteTarget(u);
    setDeleteOpen(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteSaving(true);
      setError(null);
      await axiosInstance.delete(`/users/${deleteTarget.id}`);
      const removedId = deleteTarget.id;
      setUsers((prev) => prev.filter((u) => u.id !== removedId));
      if (selectedId === removedId) {
        setSelectedId((prevSel) => {
          const remaining = (users || []).filter((u) => u.id !== removedId);
          const me = remaining.find((u) => u.id === user?.id) || remaining[0] || null;
          return me ? me.id : null;
        });
      }
      if (user && user.id === removedId) {
        try {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        } catch (_) {}
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
      }
      setDeleteOpen(false);
      setDeleteTarget(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to delete user.';
      setError(msg);
      console.error(e);
    } finally {
      setDeleteSaving(false);
    }
  };

  const avatarAccent =
    selected?.is_super_admin
      ? 'from-amber-500 to-yellow-400'
      : selected?.role === 'admin'
      ? 'from-blue-500 to-sky-400'
      : 'from-emerald-500 to-teal-400';

  return (
    <div className="settings-page space-y-5 max-w-[1400px] mx-auto">
      {error && (
        <div
          className="stagger-in sticky top-0 z-50 rounded-xl px-4 py-3 text-[13px] font-semibold flex items-center justify-between"
          style={{
            background: 'rgba(248,113,113,0.12)',
            border: '1px solid rgba(248,113,113,0.35)',
            color: '#fecaca',
          }}
        >
          <span className="flex items-center gap-2">
            <XCircle size={16} /> {error}
          </span>
          <button onClick={() => setError(null)} className="text-[12px] font-bold opacity-80 hover:opacity-100">
            DISMISS
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
        <StatCard title="Total Admins" value={counts.total} icon={Users} staggerIdx={0} />
        <StatCard
          title="Super Admins"
          value={counts.superAdmins}
          icon={ShieldCheck}
          accent="var(--nad-accent-txt)"
          staggerIdx={1}
        />
        <StatCard title="Admins" value={counts.admins} icon={Shield} accent="#60a5fa" staggerIdx={2} />
        <StatCard title="Employees" value={counts.employees} icon={Eye} accent="#34d399" staggerIdx={3} />
      </div>

      {/* Main layout: Enrolled Users list + Detail panel */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left: Enrolled Users */}
        <div className={stagger(4).className} style={stagger(4).style}>
          <Card3D
            className="content-glass rounded-2xl p-5 flex flex-col h-full"
            intensity={5}
            lift={12}
            tilt={false}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[14px] font-bold" style={{ color: 'var(--nad-t1)' }}>
                Enrolled Users
              </h3>
              <button
                type="button"
                onClick={() => setEnrollOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                  color: '#1e293b',
                  border: '1px solid rgba(250,204,21,0.6)',
                  boxShadow: '0 10px 24px -10px rgba(250,204,21,0.45)',
                }}
              >
                <UserPlus size={13} /> Enroll
              </button>
            </div>

            <div className="relative mb-3.5">
              <Search
                size={14}
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--nad-t3)',
                }}
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-9 pr-3 py-2.5 rounded-xl text-[13px] outline-none transition-colors"
                style={{
                  background: 'var(--nad-s1)',
                  color: 'var(--nad-t1)',
                  border: '1px solid var(--nad-b2)',
                }}
              />
            </div>

            <div className="flex-1 overflow-y-auto max-h-[620px] pr-1 space-y-1.5">
              {loading ? (
                <div className="text-center py-10 text-[12px]" style={{ color: 'var(--nad-t3)' }}>
                  Loading users…
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-10">
                  <EyeOff size={24} style={{ color: 'var(--nad-t4)', margin: '0 auto 8px' }} />
                  <p className="text-[12px]" style={{ color: 'var(--nad-t3)' }}>
                    No users match your search.
                  </p>
                </div>
              ) : (
                filtered.map((u) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    selected={u.id === selectedId}
                    onClick={() => setSelectedId(u.id)}
                    isSelf={u.id === user?.id}
                    onEdit={openEdit}
                    onDelete={openDelete}
                    editing={editSaving && editTarget?.id === u.id}
                    deleting={deleteSaving && deleteTarget?.id === u.id}
                  />
                ))
              )}
            </div>
          </Card3D>
        </div>

        {/* Right: Detail panel */}
        <div
          className={`lg:col-span-3 ${stagger(5).className}`}
          style={stagger(5).style}
        >
          <Card3D
            className="content-glass rounded-2xl p-5 h-full flex flex-col"
            intensity={5}
            lift={12}
            tilt={false}
          >
            {!selected ? (
              <div className="h-full flex items-center justify-center py-16">
                <div className="text-center">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3"
                    style={{ background: 'var(--nad-s1)', border: '1px solid var(--nad-b2)' }}
                  >
                    <Users size={22} style={{ color: 'var(--nad-t3)' }} />
                  </div>
                  <p className="text-[13px] font-semibold mb-1" style={{ color: 'var(--nad-t1)' }}>
                    Select a user
                  </p>
                  <p className="text-[11px]" style={{ color: 'var(--nad-t3)' }}>
                    Pick a user on the left to edit their access.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Header: identity */}
                <div className="flex items-start gap-4 pb-5 mb-5" style={{ borderBottom: '1px solid var(--nad-b1)' }}>
                  <div
                    className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${avatarAccent} flex items-center justify-center flex-shrink-0 ring-2 ring-white/10 shadow-lg`}
                  >
                    <span className="text-white font-extrabold text-[16px]">
                      {getInitials(selected.full_name)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-[16px] font-extrabold tracking-tight" style={{ color: 'var(--nad-t1)' }}>
                      {selected.full_name || '—'}
                    </h4>
                    <p className="text-[12px]" style={{ color: 'var(--nad-t3)' }}>
                      {selected.email || '—'}
                    </p>
                    <p className="text-[11px] mt-0.5" style={{ color: 'var(--nad-t4)' }}>
                      {effectiveRoleLabel(selected)} · {formatDate(selected.created_at)}
                    </p>
                  </div>
                </div>

                {/* Account Active */}
                <div className="flex items-center justify-between mb-5 px-4 py-3 rounded-xl" style={{ background: 'var(--nad-s1)', border: '1px solid var(--nad-b2)' }}>
                  <div>
                    <p className="text-[12px] font-bold" style={{ color: 'var(--nad-t1)' }}>
                      Account Active
                    </p>
                    <p className="text-[10.5px] mt-0.5" style={{ color: 'var(--nad-t4)' }}>
                      Suspended users lose all module access.
                    </p>
                  </div>
                  <CircleSwitch
                    checked={!!draft?.is_active}
                    onChange={handleToggleActive}
                  />
                </div>

                {/* Side Panel Access */}
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-[12px] font-bold" style={{ color: 'var(--nad-t1)' }}>
                          Side Panel Access
                        </p>
                        <span
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                          style={{
                            background: 'var(--nad-s1)',
                            color: 'var(--nad-t3)',
                            border: '1px solid var(--nad-b2)',
                          }}
                        >
                          {visibleCount.visible}/{visibleCount.total} visible
                        </span>
                      </div>
                      <p className="text-[10.5px] mt-1" style={{ color: 'var(--nad-t4)' }}>
                        Control visibility and set the access level (S.Admin / Admin / Employee) independently per side-panel button. Buttons that are switched off are hidden from that user&apos;s side panel entirely.
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <TinyPill label="All" active={false} onClick={() => handleBulkVisible(true)} />
                      <TinyPill label="None" active={false} onClick={() => handleBulkVisible(false)} />
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto max-h-[420px] pr-1 space-y-1.5">
                    {(() => {
                      const targetIsSettingsOwner = isSettingsOwnerOf(selected);
                      return menuItems.map((item) => (
                        <ModuleAccessRow
                          key={item.id}
                          item={item}
                          access={draft?.module_access?.[item.id]}
                          onChange={handleModuleChange}
                          onSubmenuChange={handleSubmenuChange}
                          onToggleExpand={toggleRowExpanded}
                          expanded={!!expandedRows[item.id]}
                          targetIsSettingsOwner={targetIsSettingsOwner}
                        />
                      ));
                    })()}
                  </div>
                </div>

                {/* Save / Footer */}
                <div
                  className="flex items-center justify-between mt-5 pt-5"
                  style={{ borderTop: '1px solid var(--nad-b1)' }}
                >
                  <p className="text-[10.5px]" style={{ color: 'var(--nad-t4)' }}>
                    Each module has an independent access level &amp; visibility. Changes take effect the next time this user logs in or refreshes.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const ma = cloneModuleAccess(
                          selected.module_access ||
                            defaultModuleAccessFor(selected.role, !!selected.is_super_admin, selected.email)
                        );
                        setDraft({
                          role: selected.is_super_admin ? 'admin' : selected.role,
                          is_super_admin: !!selected.is_super_admin,
                          is_active: selected.is_active !== false,
                          module_access: ma,
                        });
                      }}
                      disabled={!hasChanges || saving}
                      className="px-4 py-2 rounded-xl text-[12px] font-semibold"
                      style={{
                        background: 'var(--nad-s1)',
                        color: 'var(--nad-t2)',
                        border: '1px solid var(--nad-b2)',
                        opacity: !hasChanges ? 0.55 : 1,
                        cursor: !hasChanges ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      onClick={saveChanges}
                      disabled={!hasChanges || saving}
                      className="px-4 py-2 rounded-xl text-[12px] font-bold flex items-center gap-1.5 transition-all duration-200"
                      style={{
                        background: hasChanges
                          ? 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                          : 'var(--nad-s2)',
                        color: hasChanges ? '#1e293b' : 'var(--nad-t3)',
                        border: '1px solid',
                        borderColor: hasChanges ? 'rgba(250,204,21,0.6)' : 'var(--nad-b2)',
                        boxShadow: hasChanges ? '0 10px 24px -8px rgba(250,204,21,0.45)' : undefined,
                        opacity: saving ? 0.7 : 1,
                        cursor: !hasChanges ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <Check size={13} />
                      {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </Card3D>
        </div>
      </div>

      {/* User Type Reference */}
      <div className={stagger(6).className} style={stagger(6).style}>
        <Card3D className="content-glass rounded-2xl p-5" intensity={4} lift={10} tilt={false}>
          <h3 className="text-[14px] font-bold mb-4" style={{ color: 'var(--nad-t1)' }}>
            User Type Reference
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
            <div
              className="p-4 rounded-xl"
              style={{
                background: 'linear-gradient(135deg, rgba(250,204,21,0.10), rgba(250,204,21,0.04))',
                border: '1px solid rgba(250,204,21,0.35)',
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(250,204,21,0.25)', color: 'var(--nad-accent-txt)' }}
                >
                  <ShieldCheck size={14} />
                </div>
                <p className="text-[13px] font-extrabold" style={{ color: 'var(--nad-accent-txt)' }}>
                  Super Admin
                </p>
              </div>
              <p className="text-[11px] font-bold mb-1 flex items-center gap-1" style={{ color: 'var(--nad-accent-txt)' }}>
                <Check size={11} /> FULL ACCESS
              </p>
              <p className="text-[11.5px] leading-relaxed" style={{ color: 'var(--nad-t3)' }}>
                Unrestricted access to every module plus user &amp; system configuration.
              </p>
            </div>

            <div
              className="p-4 rounded-xl"
              style={{
                background: 'linear-gradient(135deg, rgba(59,130,246,0.10), rgba(59,130,246,0.04))',
                border: '1px solid rgba(59,130,246,0.35)',
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(59,130,246,0.22)', color: '#60a5fa' }}
                >
                  <Shield size={14} />
                </div>
                <p className="text-[13px] font-extrabold" style={{ color: '#93c5fd' }}>
                  Admin
                </p>
              </div>
              <p className="text-[11px] font-bold mb-1 flex items-center gap-1" style={{ color: '#93c5fd' }}>
                <Check size={11} /> LIMITED ACCESS
              </p>
              <p className="text-[11.5px] leading-relaxed" style={{ color: 'var(--nad-t3)' }}>
                Can create, edit and manage records in permitted modules. Cannot configure the system.
              </p>
            </div>

            <div
              className="p-4 rounded-xl"
              style={{
                background: 'linear-gradient(135deg, rgba(16,185,129,0.10), rgba(16,185,129,0.04))',
                border: '1px solid rgba(16,185,129,0.35)',
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(16,185,129,0.22)', color: '#34d399' }}
                >
                  <Eye size={14} />
                </div>
                <p className="text-[13px] font-extrabold" style={{ color: '#6ee7b7' }}>
                  Employee
                </p>
              </div>
              <p className="text-[11px] font-bold mb-1 flex items-center gap-1" style={{ color: '#6ee7b7' }}>
                <Check size={11} /> READ ONLY
              </p>
              <p className="text-[11.5px] leading-relaxed" style={{ color: 'var(--nad-t3)' }}>
                May view permitted modules only. All create, edit and delete actions are disabled.
              </p>
            </div>
          </div>
        </Card3D>
      </div>

      <EnrollDialog
        open={enrollOpen}
        onClose={() => !enrolling && setEnrollOpen(false)}
        onSubmit={handleEnroll}
        submitting={enrolling}
      />

      <EditUserDialog
        open={editOpen}
        onClose={() => !editSaving && (setEditOpen(false), setEditTarget(null))}
        onSubmit={handleEditSubmit}
        submitting={editSaving}
        user={editTarget}
      />

      <DeleteUserDialog
        open={deleteOpen}
        onClose={() => !deleteSaving && (setDeleteOpen(false), setDeleteTarget(null))}
        onConfirm={handleDeleteConfirm}
        confirming={deleteSaving}
        user={deleteTarget}
      />
    </div>
  );
}
